import { FC, useState } from 'react';
import { Button, Empty, Form, Modal, Input, Row, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';
import { AppointmentById } from 'gql/appointments/__generated__/AppointmentById';
import { CreateAppointmentNote } from 'gql/appointments/__generated__/CreateAppointmentNote';
import { CREATE_APPOINTMENT_NOTE } from 'gql/appointments/mutation';
import client from 'apolloClient';
import { APPOINTMENT_CREATOR } from 'constants/appointments';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useUIDispatchContext } from 'providers/UI';
import translateIcon from 'assets/icons/translation.svg';

import { CardNote } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { TranslationModal } from 'components/shared';

const { TextArea } = Input;

const Notes: FC = () => {
  // translations
  const { t } = useTranslation();
  // form
  const [form] = Form.useForm();
  // states
  const [isModalVisible, setIsModalVisible] = useState(false);
  // navigation
  const params = useParams();
  // context
  const { toggleModal } = useUIDispatchContext();

  // graphql
  const closeNotes = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const { data } = useQuery<AppointmentById>(GET_APPOINTMENT_ITEM, {
    skip: !params.id,
    variables: {
      id: params.id
    },
    fetchPolicy: 'cache-only'
  });

  const [createNote] = useMutation<CreateAppointmentNote>(
    CREATE_APPOINTMENT_NOTE,
    {
      onCompleted() {
        client.refetchQueries({ include: [GET_APPOINTMENT_ITEM] });
        closeNotes();
      },
      onError: err => showErrorMessage(err)
    }
  );

  const appointment = data?.appointment?.data;

  const showModal = () => {
    setIsModalVisible(true);
  };

  const onFinish = (values: { note: string }) => {
    createNote({
      variables: {
        id: params.id,
        input: {
          note: values.note
        }
      }
    });
  };

  const addButton = (
    <Button onClick={showModal} icon={<PlusOutlined />}>
      {t('appointments_details.add_note')}
    </Button>
  );

  const notes = appointment?.notes || [];

  return (
    <>
      {!!notes.length && (
        <Row justify="start" style={{ paddingBottom: 26 }}>
          <Button
            type="primary"
            onClick={() =>
              toggleModal('translation', true, {
                data: appointment?.notes,
                appointment_id: appointment?.id
              })
            }
          >
            <Image
              src={translateIcon}
              width={20}
              color="white"
              preview={false}
              style={{ paddingRight: 6 }}
            />
            {t('appointments.translate_note')}
          </Button>
        </Row>
      )}
      {notes.length ? (
        notes.map(item => (
          <CardNote
            key={`card-item-${item.id}`}
            title={
              item.sender === APPOINTMENT_CREATOR.doctor
                ? t('appointments.your_message')
                : `${appointment?.patient.first_name} ${appointment?.patient.last_name}`
            }
            sender={item.sender}
            content={item?.note || ''}
            date={dayjs(item?.created_at).format(FULL_DATE_FORMAT)}
          />
        ))
      ) : (
        <Empty>{addButton}</Empty>
      )}
      {!!notes.length && addButton}
      <Modal
        title={t('appointments_details.add_note')}
        open={isModalVisible}
        onOk={form.submit}
        onCancel={closeNotes}
        cancelButtonProps={{ shape: 'round' }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        okButtonProps={{ shape: 'round' }}
      >
        <Form form={form} onFinish={onFinish}>
          <Form.Item name="note" rules={[{ required: true }]}>
            <TextArea placeholder="" />
          </Form.Item>
        </Form>
      </Modal>
      <TranslationModal />
    </>
  );
};

export default Notes;
